import {
  PAYMENT_DEFAULTS,
  CURRENCY,
  getService,
  getChargeAmount,
  getMode,
} from "./config";

/**
 * paymentService — a single, forward-compatible entry point for starting a
 * payment. The UI always calls `initiatePayment({ serviceId, customer })`;
 * only this file knows HOW the payment happens for the current mode. When a
 * FastAPI backend is added later, only the "backend_checkout" branch changes —
 * the UI stays exactly the same.
 */

export const isPaymentConfigured = (serviceId) => {
  const service = getService(serviceId);
  if (!service || service.paymentType === "enquiry") return false;
  const mode = getMode(service);
  if (mode === "payment_link") return Boolean(service.razorpay?.paymentLinkUrl);
  if (mode === "payment_button") return Boolean(service.razorpay?.paymentButtonId);
  if (mode === "backend_checkout") return Boolean(PAYMENT_DEFAULTS.keyId);
  return false;
};

// Attach Razorpay-supported prefill params to a Payment Link URL when possible.
const withPrefill = (url, customer = {}) => {
  try {
    const u = new URL(url);
    if (customer.name) u.searchParams.set("prefill[name]", customer.name);
    if (customer.email) u.searchParams.set("prefill[email]", customer.email);
    if (customer.phone) u.searchParams.set("prefill[contact]", customer.phone);
    return u.toString();
  } catch (e) {
    return url;
  }
};

/**
 * initiatePayment
 * @param {{ serviceId?: string, customer?: {name,email,phone} }} args
 * @returns {Promise<{status: 'redirected'|'unconfigured'|'hosted'|'enquiry'|'error'}>}
 */
export async function initiatePayment({ serviceId, customer } = {}) {
  const service = getService(serviceId);
  if (!service) return { status: "error" };
  if (service.paymentType === "enquiry") return { status: "enquiry" };

  const mode = getMode(service);

  if (!isPaymentConfigured(serviceId)) {
    return { status: "unconfigured" };
  }

  if (mode === "payment_link") {
    const url = withPrefill(service.razorpay.paymentLinkUrl, customer);
    window.open(url, "_blank", "noopener,noreferrer");
    return { status: "redirected" };
  }

  if (mode === "payment_button") {
    // Rendered by the embedded <RazorpayPaymentButton /> component instead.
    return { status: "hosted" };
  }

  // ── FUTURE: full Razorpay Checkout via FastAPI backend ──────────────────
  // Enable by setting PAYMENT_DEFAULTS.mode = "backend_checkout" and keyId.
  // Nothing in the UI needs to change — only this branch runs instead.
  if (mode === "backend_checkout") {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service: service.name,
          amount: getChargeAmount(service) * 100, // paise
          currency: CURRENCY,
        }),
      });
      const order = await res.json();
      return await new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: PAYMENT_DEFAULTS.keyId,
          amount: order.amount,
          currency: order.currency,
          order_id: order.id,
          name: "Svara Chakra",
          description: `${service.name} — ${service.paymentType === "full" ? "Energy Exchange" : "Session Reservation"}`,
          prefill: {
            name: customer?.name,
            email: customer?.email,
            contact: customer?.phone,
          },
          handler: () => resolve({ status: "hosted" }),
        });
        rzp.open();
      });
    } catch (e) {
      return { status: "error" };
    }
  }

  return { status: "error" };
}
